/* eslint-disable max-lines */
/* eslint-disable max-params */
import { defineStore } from 'pinia'

import { updatePrivacyPolicy, updateReceiveNewsletter } from '~/entities/user/user/api/passport-users'
import {
  addContact,
  addCurrentUserHiddenHlContent,
  addSites,
  addSubscription,
  checkStatus,
  createEducation,
  createExperience,
  createInterest,
  createLanguage,
  createSkill,
  deleteEducation,
  deleteExperience,
  deleteInterest,
  deleteLanguage,
  deleteSkill,
  editEmail,
  editLocation,
  editName,
  editSite,
  getCountry,
  getCurrentUser,
  getCurrentUserHiddenHlContent,
  getFields,
  getLangList,
  getSubscription,
  getUser,
  getUserByLogin,
  removeSite,
  removeSubscription,
  saveResume,
  updateEducation,
  updateExperience,
  updateLanguage,
  updatePhone,
  updateSkill,
  uploadPicture,
  uploadPreviewPicture,
} from '~/entities/user/user/api/users'
import type { IUser } from '~/entities/user/user/interfaces/user'

import type { IFedSearchInfo } from '~/features-shared/member/interfaces/search'
import {
  setConversationSubscribed,
} from '~/features-shared/rtconv/api/rtinterface-senders'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'

type UserSearchInfo = Pick<IFedSearchInfo, 'login' | 'full_name'>

interface UsersState {
  users: Map<string, IUser>
  searchedUsers: Map<string, UserSearchInfo>
  subscriptions: Set<string>
  displayHlContent: Map<string, boolean>
  currentUserLogin: string
}

function filterById(el: { id: string }, id: string): boolean {
  return el.id === id
}

export const useUsersStore = defineStore({
  id: 'users',
  state: () => ({
    users: new Map(),
    searchedUsers: new Map(),
    subscriptions: new Set(),
    displayHlContent: new Map(),
    currentUserLogin: '',
  } as UsersState),
  getters: {
    // TODO: Get the real current user
    currentUser: state => state.users.get(state.currentUserLogin),

    // eslint-disable-next-line complexity, max-statements
    completion: (state) => {
      let percent = 0
      const user = state.users.get(state.currentUserLogin)
      const isProfileWorkforceActivated = useFeatureManager('profile.3dworkforce')
      // About
      if (user?.tenant_more_about_me)
        percent += 5

      // Profile picture
      if (user?.picture_processed === '1')
        percent += 30

      // Interests
      const INTEREST_WEIGHT = 5
      percent += Math.min(2, user?.interests?.length || 0) * INTEREST_WEIGHT

      // Languages
      const LANGUAGE_WEIGHT = 5
      if (isProfileWorkforceActivated.value)
        percent += Math.min(2, user?.workforce?.nbLanguages || 0) * LANGUAGE_WEIGHT
      else
        percent += Math.min(2, user?.languages?.length || 0) * LANGUAGE_WEIGHT

      // Skills
      const SKILL_WEIGHT = 10

      if (isProfileWorkforceActivated.value)
        percent += Math.min(3, user?.workforce?.nbSkills || 0) * SKILL_WEIGHT
      else
        percent += Math.min(3, user?.skills?.length || 0) * SKILL_WEIGHT

      // Schools
      const SCHOOL_WEIGHT = 5
      percent += Math.min(1, user?.schools?.length || 0) * SCHOOL_WEIGHT

      // Works
      const WORK_WEIGHT = 5
      percent += Math.min(2, user?.works?.length || 0) * WORK_WEIGHT

      return percent
    },
  },
  actions: {
    async fetchCurrentUser() {
      const user = (await getCurrentUser()).result
      this.currentUserLogin = user.login
      this.mergeUser(user)
    },

    async fetchUser(hash_key: string) {
      const user = (await getUser(hash_key)).result
      this.mergeUser(user)
    },

    async fetchUserByLogin(login: string) {
      const user = (await getUserByLogin(login)).result
      this.mergeUser(user)
    },

    /**
     * @description Provide a user from its login or hash_key.
     *              If the user has not been fetched yet, fetch it
     * @param userId can be either a login or a hash_key
     * @returns the user
     */
    async getUser(userId: string) {
      let user = this.users.get(userId)
      if (!user) {
        // fetch user
        if (userId.length > 6)
          await this.fetchUser(userId) // with hash_key
        else
          await this.fetchUserByLogin(userId) // with login

        user = this.users.get(userId)
      }

      return user
    },

    /**
     * Provide a user display name
     * @param user A user
     * @returns The user display name or the login if no first name or no last name.
     */
    getUserDisplayName(user: IUser): string {
      return user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.login
    },

    mergeUser(user: IUser) {
      const u = this.users.get(user.login)
      if (u)
        Object.assign(u, user)
      else
        this.users.set(user.login, user)

      // TODO: bypass dup the user with the hashkey
      const _user = this.users.get(user.login)
      if (user.hash_key && _user)
        this.users.set(user.hash_key, _user)
      /* this.users.set(user.works, user.works?.map(work => ({ ...work })).sort((a, b) => {
        const dateBeginA = a.date_begin
        const dateBeginB = b.date_begin.toLowerCase()
        if (dateBeginA < dateBeginB)
          return -1
        if (dateBeginA > dateBeginB)
          return 1
        return 0
      })) */
    },

    addSearchedUser(user: UserSearchInfo) {
      if (user.login) {
        this.searchedUsers.set(user.login, {
          login: user.login,
          full_name: user.full_name,
        })
      }
    },

    async fetchSubscription(subject_uri: string) {
      if (subject_uri.split(':')[4] !== 'rt') {
        const response = await getSubscription(subject_uri)
        if (response.result?.subjectUri)
          this.subscriptions.add(subject_uri)
      }
    },

    async addSubscription(subject_uri: string) {
      if (subject_uri.split(':')[4] !== 'rt') {
        this.subscriptions.add(subject_uri)
        await addSubscription(subject_uri)
      }
      else {
        setConversationSubscribed({
          convId: Number(subject_uri.split(':')
            .pop()),
          isSubscribed: true,
          subjectUri: subject_uri,
          waitForResponse: false,
        })
      }
    },

    async removeSubscription(subject_uri: string) {
      if (subject_uri.split(':')[4] !== 'rt') {
        this.subscriptions.delete(subject_uri)
        await removeSubscription(subject_uri)
      }
      else {
        setConversationSubscribed({
          convId: Number(subject_uri.split(':')
            .pop()),
          isSubscribed: false,
          subjectUri: subject_uri,
          waitForResponse: false,
        })
      }
    },

    async addHiddenHlContent(community_id: string) {
      await addCurrentUserHiddenHlContent(community_id)
      this.displayHlContent.set(community_id, false)
    },

    async removeHiddenHlContent(community_id: string) {
      this.displayHlContent.set(community_id, true)
    },

    async getHiddenHlContent(community_id?: string) {
      if (!community_id || this.displayHlContent.has(community_id))
        return
      this.displayHlContent.set(community_id, false)
      const response = await getCurrentUserHiddenHlContent(community_id)
      if (!response)
        this.displayHlContent.set(community_id, true)
    },

    // TODO
    // #region TO MOVE TO profile.ts composable
    async uploadPreviewPic(base64_encoded: boolean, file_content: string) {
      return uploadPreviewPicture(base64_encoded, file_content)
    },

    async getFields() {
      const response = await getFields()
      return response
    },

    async uploadPic(fileKey: string, filename: string) {
      await uploadPicture(fileKey, filename)
      if (this.currentUser)
        this.currentUser.user_picture_fingerprint = fileKey
    },

    async editResume(resume: string) {
      const currentUser = this.currentUser

      const oldResume = currentUser?.tenant_more_about_me
      await saveResume(resume)

      if (!currentUser)
        return

      currentUser.tenant_more_about_me = resume

      if (oldResume === '' && currentUser?.tenant_more_about_me)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) + 5
      if (currentUser?.tenant_more_about_me === '')
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) - 5
    },

    async editName(
      firstName: string,
      lastName: string,
      login: string,
    ) {
      await editName(
        firstName,
        lastName,
        login,
      )
        .then((response) => {
          if (this.currentUser) {
            this.currentUser.first_name = response.fields.firstName.value
            this.currentUser.last_name = response.fields.lastName.value
          }
        })
    },

    async editPhone(telephone: string, username: string) {
      await updatePhone(telephone, username)
        .then(() => {
          if (this.currentUser)
            this.currentUser.tel = telephone
        })
    },

    async editEmail(email: string, username: string) {
      await editEmail(email, username)
        .then(() => {
          if (this.currentUser)
            this.currentUser.email = email
        })
    },

    async editLocation(data: {
      company?: string
      address?: string
      city?: string
      zip?: string
      country?: string
      username: string
    }) {
      let iso3
      if (data.country) {
        const count = await getCountry()
        const newCountry = count.find(countryUser => countryUser.label === data.country)
        iso3 = newCountry!.iso3
      }
      await editLocation({
        company: data.company,
        address: data.address,
        city: data.city,
        zip: data.zip,
        country: iso3,
        username: data.username,
      })
        .then(() => {
          if (this.currentUser) {
            if (data.company)
              this.currentUser.company = data.company
            if (data.address)
              this.currentUser.address = data.address
            if (data.city)
              this.currentUser.city = data.city
            if (data.zip)
              this.currentUser.zip = data.zip
            if (data.country)
              this.currentUser.country = data.country
          }
        })

      // this.currentUser.country_code =
    },

    async addWebsites(
      url: string,
      type: string,
      title: string,
      description: string,
      update: string,
    ) {
      const response = await addSites(url, type, title, description, update)
      this.currentUser?.links?.push(response.result)
    },

    async editWebsite(
      url: string,
      type: string,
      title: string,
      description: string,
      update: string,
      id: string,
    ) {
      await editSite(
        url,
        type,
        title,
        description,
        update,
        id,
      )
      const index = this.currentUser?.links?.findIndex(link => filterById(link, id))

      if (typeof index === 'number') {
        this.currentUser?.links?.splice(
          index,
          1,
          { url, type, title, description, update, id },
        )
      }
    },

    async getLinkById(id: string) {
      const link = this.currentUser?.links?.find(link => filterById(link, id))
      return link
    },

    async deleteWebsite(id: string) {
      const index = this.currentUser?.links?.findIndex(link => filterById(link, id))
      if (typeof index === 'number')
        this.currentUser?.links?.splice(index, 1)

      await removeSite(id)
    },

    async addSkill(
      title: string,
      level: number,
      description: string,
      update: string,
    ) {
      const response = await createSkill(
        title,
        level,
        description,
        update,
      )
      const currentUser = this.currentUser

      if (!currentUser)
        return

      currentUser.skills?.push(response.result)
      if (currentUser.skills && currentUser.skills?.length < 4)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) + 10
    },

    async getSkillById(id: string) {
      const skill = this.currentUser?.skills?.find(skill => filterById(skill, id))
      return skill
    },

    async updateSkill(
      title: string,
      level: number,
      description: string,
      update: string,
      id: string,
    ) {
      await updateSkill(
        title,
        level,
        description,
        update,
        id,
      )
      const index = this.currentUser?.skills?.findIndex(skill => filterById(skill, id))
      if (typeof index === 'number') {
        this.currentUser?.skills?.splice(
          index,
          1,
          { title, level, description, id, update },
        )
      }
    },

    async removeSkill(id: string) {
      const currentUser = this.currentUser

      const index = this.currentUser?.skills?.findIndex(skill => filterById(skill, id))
      if (typeof index === 'number')
        currentUser?.skills?.splice(index, 1)

      await deleteSkill(id)

      if (currentUser && currentUser.skills && currentUser.skills?.length < 3)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) - 10
    },

    async addLanguage(model: { description: string, level: number, language: string }) {
      const lang = (await getLangList()).result
      const newLanguage = lang.find(languageUser => languageUser.lang_english === model.language)!
      const response = await createLanguage({
        description: model.description,
        iso_code: newLanguage.iso_code,
        lang_english: model.language,
        lang_native: newLanguage.lang_native,
        language_id: newLanguage.id,
        level: model.level,
        translated: model.language,
        update: new Date()
          .toISOString(),
      })

      this.currentUser?.languages?.push(response.result)
      if ((this.currentUser?.languages?.length || 0) < 3)
        this.currentUser.profile_completion = this.currentUser?.profile_completion + 5
    },

    async updateLanguage(model: { description: string, level: number, language: string, id: string }) {
      const lang = (await getLangList()).result
      const newLanguage = lang.find(languageUser => languageUser.lang_english === model.language)!
      const update = new Date()
        .toISOString()

      await updateLanguage({
        description: model.description,
        language_id: newLanguage.id,
        level: model.level,
        update,
        id: model.id,
      })
      const index = this.currentUser?.languages?.findIndex(language => filterById(language, model.id))

      if (typeof index === 'number') {
        this.currentUser?.languages?.splice(
          index,
          1,
          {
            description: model.description,
            iso_code: newLanguage.iso_code,
            lang_english: model.language,
            lang_native: newLanguage.lang_native,
            language_id: newLanguage.id,
            level: model.level,
            update,
            id: model.id,
          },
        )
      }
    },

    async getLanguageById(id: string) {
      const Language = this.currentUser?.languages?.find(language => filterById(language, id))
      return Language
    },

    async removeLanguage(id: string) {
      const currentUser = this.currentUser

      const index = this.currentUser?.languages?.findIndex(language => filterById(language, id))
      if (typeof index === 'number')
        currentUser?.languages?.splice(index, 1)

      await deleteLanguage(id)

      if (currentUser && currentUser.languages && currentUser?.languages?.length < 2)
        currentUser.profile_completion = (currentUser?.profile_completion ?? 0) - 5
    },

    async addInterest(title: string, update: string) {
      const currentUser = this.currentUser

      const response = await createInterest(title, update)
      currentUser?.interests?.push(response.result)

      if (currentUser && currentUser.interests && currentUser?.interests?.length < 3)
        currentUser.profile_completion = (currentUser?.profile_completion ?? 0) + 5
    },

    async deleteInterest(id: string) {
      const currentUser = this.currentUser

      const index = this.currentUser?.interests?.findIndex(interest => filterById(interest, id))
      if (typeof index === 'number')
        currentUser?.interests?.splice(index, 1)

      await deleteInterest(id)

      if (currentUser && currentUser.interests && currentUser.interests.length < 2)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) - 5
    },

    async getInterestById(id: string) {
      const interest = this.currentUser?.interests?.find(interest => filterById(interest, id))
      return interest
    },

    async addExperience(
      title: string,
      work_name: string,
      industry: string,
      place: string,
      description: string,
      date_begin: string,
      date_end: string,
      not_finished: string,
      update: string,
    ) {
      const currentUser = this.currentUser

      const response = await createExperience(
        title,
        work_name,
        industry,
        place,
        description,
        date_begin,
        date_end,
        not_finished,
        update,
      )

      if (!currentUser)
        return

      currentUser.works = currentUser.works || []
      currentUser.works.push(response.result)
      if (currentUser.works.length < 3)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) + 5
    },

    async getExperienceById(id: string) {
      const Experience = this.currentUser?.works?.find(experience => filterById(experience, id))
      return Experience
    },

    async updateExperience(
      title: string,
      work_name: string,
      industry: string,
      place: string,
      description: string,
      date_begin: string,
      date_end: string,
      not_finished: '0' | '1',
      update: string,
      id: string,
    ) {
      const currentUser = this.currentUser

      await updateExperience(
        title,
        work_name,
        industry,
        place,
        description,
        date_begin,
        date_end,
        not_finished,
        update,
        id,
      )
      const index = currentUser?.works?.findIndex(experience => filterById(experience, id))
      if (typeof index === 'number') {
        currentUser?.works?.splice(
          index,
          1,
          {
            crdate: currentUser?.works[index].crdate,
            title,
            work_name,
            industry,
            place,
            description,
            date_begin,
            date_end,
            not_finished,
            update,
            id,
          },
        )
      }
    },

    async deleteExperience(id: string) {
      const currentUser = this.currentUser

      const index = currentUser?.works?.findIndex(experience => filterById(experience, id))
      if (typeof index === 'number')
        currentUser?.works?.splice(index, 1)

      await deleteExperience(id)

      if (currentUser && currentUser.works && currentUser?.works?.length < 2)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) - 5
    },

    async addSchool(
      degree: string,
      description: string,
      place: string,
      school_name: string,
      not_finished: string,
      field_of_study: string,
      date_begin: string,
      date_end: string,
      update: string,
    ) {
      const currentUser = this.currentUser

      const response = await createEducation(
        degree,
        description,
        place,
        school_name,
        not_finished,
        field_of_study,
        date_begin,
        date_end,
        update,
      )

      if (!currentUser)
        return

      currentUser.schools = currentUser.schools || []
      currentUser.schools.push(response.result)
      if (currentUser.schools.length < 2)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) + 5
    },

    async getSchoolById(id: string) {
      const school = this.currentUser?.schools?.find(school => filterById(school, id))
      return school
    },

    async updateSchool(
      degree: string,
      description: string,
      place: string,
      school_name: string,
      not_finished: '0' | '1',
      field_of_study: string,
      date_begin: string,
      date_end: string,
      update: string,
      id: string,
    ) {
      const currentUser = this.currentUser

      await updateEducation(
        degree,
        description,
        place,
        school_name,
        not_finished,
        field_of_study,
        date_begin,
        date_end,
        update,
        id,
      )

      const index = currentUser?.schools?.findIndex(school => filterById(school, id))
      if (typeof index === 'number') {
        currentUser?.schools?.splice(
          index,
          1,
          {
            crdate: currentUser?.schools[index].crdate,
            degree,
            description,
            place,
            school_name,
            not_finished,
            field_of_study,
            date_begin,
            date_end,
            update,
            id,
          },
        )
      }
    },

    async deleteSchool(id: string) {
      const currentUser = this.currentUser

      const index = currentUser?.schools?.findIndex(school => filterById(school, id))
      if (typeof index === 'number')
        currentUser?.schools?.splice(index, 1)

      await deleteEducation(id)

      if (currentUser && currentUser.schools && currentUser.schools?.length < 2)
        currentUser.profile_completion = (currentUser.profile_completion ?? 0) - 5
    },

    async addContactToNetwork(
      hash_key: string,
      login: string,
      message: string,
    ) {
      const response = await checkStatus(hash_key, login)
      if (response.result.connected_user_to_user === 'none') {
        await addContact(message, hash_key)
        return 0
      }
      else {
        return 1
      }
    },

    // Update whether agree receive Newsletter
    async updateReceiveNewsletter(isAgree: boolean, userName: string) {
      return await updateReceiveNewsletter(isAgree, userName)
    },

    // Update whether agree Privacy Policy
    async updatePrivacyPolicy(isAgree: boolean) {
      return await updatePrivacyPolicy(isAgree)
    },

    // #endregion TO MOVE TO profile.ts composable
  },

})
